/** The zoom ceiling, and how big a step the zoom controls take. */
export const MAX_SCALE = 4

export const ZOOM_STEP = 0.75

export const FIT = { scale: 1, translation: { x: 0, y: 0 } }

export const clamp = (min, value, max) => Math.min(Math.max(value, min), max)

/**
 * Re-scale while holding one point of the photo still.
 *
 * Changing `s` alone drags the photo out of the frame towards the bottom right.
 * Pinning a point is what makes the buttons zoom into the middle of what is on
 * screen. It also means a second press picks up from wherever the visitor
 * panned to rather than snapping back to the middle.
 */
export const zoomAbout = (nextScale, { scale, translation }, point) => {
    const ratio = nextScale / scale

    return {
        scale: nextScale,
        translation: {
            x: point.x - ratio * (point.x - translation.x),
            y: point.y - ratio * (point.y - translation.y),
        },
    }
}

/**
 * The one place a zoom value is made valid.
 *
 * A scale of 1 has to land exactly on FIT, or a stray sub-pixel translation
 * keeps the photo off-centre and, on mobile, keeps the swipe disabled. Above 1
 * the translation is clamped so the photo keeps covering the frame: without
 * that a pan leaves the visitor looking at black, and stepping out from a
 * panned edge would store a translation past the edge and drift on the next
 * step.
 *
 * Clamped here, against the *new* scale, rather than left to MapInteraction's
 * `translationBounds`: those are read against the scale of the previous render,
 * so the first frame of a pinch from fit would have been pinned to the corner.
 */
export const keepInFrame = ({ scale, translation }, frame) =>
    scale <= 1
        ? FIT
        : {
              scale,
              translation: {
                  x: clamp(-(scale - 1) * frame.width, translation.x, 0),
                  y: clamp(-(scale - 1) * frame.height, translation.y, 0),
              },
          }

/**
 * Bounds to hand MapInteraction. Deliberately loose: the exact bounds are
 * applied in keepInFrame against the new scale. These only stop
 * MapInteraction's own defensive clamp from fighting it, and a photo can never
 * legitimately be further out than this.
 */
export const looseBounds = (frame) => ({
    xMin: -(MAX_SCALE - 1) * frame.width,
    xMax: 0,
    yMin: -(MAX_SCALE - 1) * frame.height,
    yMax: 0,
})
